import React, { useState } from 'react'
import { useDispatch } from 'react-redux'
import { FaEye, FaEyeSlash } from 'react-icons/fa'
import { Modal, InputGroup, Button } from '../../../../UI'
import { createStaff, checkStaffEmail, updateStaff } from '../../../../store/actions/staff'
import Container from './styles'

const StaffForm = ({ show, handleClose, editData = null }) => {
  const dispatch = useDispatch()
  const [currentStep, setCurrentStep] = useState(1)
  const [showPassword, setShowPassword] = useState(false)
  const isEditMode = !!editData
  
  // Form data
  const [formData, setFormData] = useState({
    firstName: editData?.firstName || '',
    lastName: editData?.lastName || '',
    email: editData?.email || '',
    phone: editData?.phone || '',
    role: editData?.role || 'STAFF',
    password: '',
    confirmPassword: '',
  })

  // Error states
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)

  const handleChange = (e) => {
    const { name, value } = e.target
    setFormData(prev => ({ ...prev, [name]: value }))
    // Clear error when user starts typing
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: '' }))
    }
  }

  const validateStep1 = async () => {
    const newErrors = {}

    if (!formData.firstName?.trim()) {
      newErrors.firstName = 'First name is required'
    }

    if (!formData.lastName?.trim()) {
      newErrors.lastName = 'Last name is required'
    }

    if (!formData.email?.trim()) {
      newErrors.email = 'Email is required'
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Email is invalid'
    } else if (!isEditMode || formData.email !== editData.email) {
      // Check if email exists only if it's create mode or email changed
      const exists = await checkStaffEmail(formData.email)
      if (exists) {
        newErrors.email = 'Email already exists'
      }
    }

    if (!formData.phone?.trim()) {
      newErrors.phone = 'Phone number is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const validateStep2 = () => {
    const newErrors = {}

    if (!formData.role) {
      newErrors.role = 'Role is required'
    }

    // Password validation - only required in create mode
    if (!isEditMode) {
      if (!formData.password) {
        newErrors.password = 'Password is required'
      } else if (formData.password.length < 6) {
        newErrors.password = 'Password must be at least 6 characters'
      }

      if (!formData.confirmPassword) {
        newErrors.confirmPassword = 'Please confirm password'
      } else if (formData.password !== formData.confirmPassword) {
        newErrors.confirmPassword = 'Passwords do not match'
      }
    } else {
      // In edit mode, validate only if password is provided
      if (formData.password) {
        if (formData.password.length < 6) {
          newErrors.password = 'Password must be at least 6 characters'
        }
        if (formData.password !== formData.confirmPassword) {
          newErrors.confirmPassword = 'Passwords do not match'
        }
      }
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleNext = async () => {
    if (await validateStep1()) {
      setCurrentStep(2)
    }
  }

  const handleBack = () => {
    setCurrentStep(1)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validateStep2()) return

    setLoading(true)

    const { confirmPassword, ...staffData } = formData
    
    // In edit mode, only send password if it's provided
    if (isEditMode && !staffData.password) {
      delete staffData.password
    }
    
    let result
    if (isEditMode) {
      result = await dispatch(updateStaff(editData.id, staffData))
    } else {
      result = await dispatch(createStaff(staffData))
    }

    setLoading(false)

    if (result.success) {
      handleClose()
      resetForm()
    }
  }

  const resetForm = () => {
    setFormData({
      firstName: '',
      lastName: '',
      email: '',
      phone: '',
      role: 'STAFF',
      password: '',
      confirmPassword: '',
    })
    setErrors({})
    setCurrentStep(1)
  }

  const onClose = () => {
    handleClose()
    resetForm()
  }

  return (
    <Modal showModal={show} onClose={onClose} showCloseBtn={true}>
      <Container>
        <div className="adminForm--container">
          <header>
            <h2>{isEditMode ? 'Edit Staff Member' : 'Add New Staff Member'}</h2>
            <p>{currentStep === 1 ? 'Enter basic information' : 'Set role and password'}</p>
          </header>

          <div className="progress-indicator">
            <div className={`step ${currentStep >= 1 ? 'active' : ''}`}>
              <div className="step-number">1</div>
              <span>Basic Info</span>
            </div>
            <div className="step-line"></div>
            <div className={`step ${currentStep >= 2 ? 'active' : ''}`}>
              <div className="step-number">2</div>
              <span>Security</span>
            </div>
          </div>

          <form onSubmit={handleSubmit}>
            {currentStep === 1 ? (
              <div className="form-step">
                <div className="input-row">
                  <InputGroup
                    label="First Name"
                    name="firstName"
                    value={formData.firstName}
                    onChange={handleChange}
                    placeholder="Enter first name"
                    required
                  />

                  <InputGroup
                    label="Last Name"
                    name="lastName"
                    value={formData.lastName}
                    onChange={handleChange}
                    placeholder="Enter last name"
                    required
                  />
                </div>
                {(errors.firstName || errors.lastName) && (
                  <div className="error-message">
                    {errors.firstName || errors.lastName}
                  </div>
                )}

                <InputGroup
                  label="Email Address"
                  name="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="Enter email address"
                  required
                />
                {errors.email && (
                  <div className="error-message">{errors.email}</div>
                )}

                <InputGroup
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="Enter phone number"
                  required
                />
                {errors.phone && (
                  <div className="error-message">{errors.phone}</div>
                )}

                <Button type="button" onClick={handleNext} full>
                  Next
                </Button>
              </div>
            ) : (
              <div className="form-step">
                <div className="input-group">
                  <label>Role</label>
                  <select
                    name="role"
                    value={formData.role}
                    onChange={handleChange}
                    className="select-input"
                  >
                    <option value="STAFF">Staff</option>
                    <option value="MANAGER">Manager</option>
                    <option value="DISPATCHER">Dispatcher</option>
                  </select>
                </div>
                {errors.role && (
                  <div className="error-message">{errors.role}</div>
                )}

                <div className="input-group">
                  <label>Password {isEditMode && <span style={{ fontSize: '0.8em', color: 'rgba(255, 255, 255, 0.5)' }}>(Leave blank to keep current)</span>}</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="password"
                      value={formData.password}
                      onChange={handleChange}
                      placeholder={isEditMode ? 'Enter new password (optional)' : 'Enter password'}
                      required={!isEditMode}
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                {errors.password && (
                  <div className="error-message">{errors.password}</div>
                )}

                <div className="input-group">
                  <label>Confirm Password</label>
                  <div className="password-input-wrapper">
                    <input
                      type={showPassword ? 'text' : 'password'}
                      name="confirmPassword"
                      value={formData.confirmPassword}
                      onChange={handleChange}
                      placeholder="Confirm password"
                      required
                    />
                    <button
                      type="button"
                      className="password-toggle"
                      onClick={() => setShowPassword(!showPassword)}
                    >
                      {showPassword ? <FaEyeSlash /> : <FaEye />}
                    </button>
                  </div>
                </div>
                {errors.confirmPassword && (
                  <div className="error-message">{errors.confirmPassword}</div>
                )}

                <div className="button-row">
                  <Button type="button" onClick={handleBack} className="btn-secondary">
                    Back
                  </Button>
                  <Button type="submit" disabled={loading}>
                    {loading ? (isEditMode ? 'Updating...' : 'Adding...') : (isEditMode ? 'Update Staff' : 'Add Staff')}
                  </Button>
                </div>
              </div>
            )}
          </form>
        </div>
      </Container>
    </Modal>
  )
}

export default StaffForm
